// ProjectCompletionRulesDialog.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  Button,
  Box,
  Typography,
  Divider,
} from '@mui/material';

function ProjectCompletionRulesDialog({
  open,
  onClose,
  project,
  onSave, // (payload) => void, will call API
}) {
  const [mode, setMode] = React.useState('none'); // 'none' | 'owner' | 'admin'
  const [ownerForAdmin, setOwnerForAdmin] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;

    if (!project) {
      setMode('none');
      setOwnerForAdmin(false);
      return;
    }

    setMode(project.completion_approval_mode || 'none');
    setOwnerForAdmin(!!project.completion_owner_review_for_admin);
  }, [project, open]);

  const handleSave = () => {
    if (!onSave) return;

    onSave({
      completion_approval_mode: mode,
      // Only meaningful in "admin" mode; otherwise force false
      completion_owner_review_for_admin:
        mode === 'admin' ? ownerForAdmin : false,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Task completion rules</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {project?.name
              ? `These rules apply only to project: "${project.name}".`
              : 'Control who must approve a task before it is fully closed and archived.'}
          </Typography>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Who should approve completed tasks?
        </Typography>

        <RadioGroup
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <FormControlLabel
            value="none"
            control={<Radio />}
            label="No approval – mark completed & auto-archive"
          />
          <FormControlLabel
            value="owner"
            control={<Radio />}
            label="Project owner must approve"
          />
          <FormControlLabel
            value="admin"
            control={<Radio />}
            label="Project admin must approve"
          />
        </RadioGroup>

        {mode === 'admin' && (
          <>
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={ownerForAdmin}
                  onChange={(e) => setOwnerForAdmin(e.target.checked)}
                />
              }
              label="If an admin completes a task, send to owner for approval"
            />
            <Typography variant="caption" color="text.secondary">
              When enabled, admin-completed tasks will wait for the project owner&apos;s approval.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save rules
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProjectCompletionRulesDialog;
